import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import * as classnames from 'classnames';
import { CircularProgress } from '@mui/material';
import { DownloadTrackButton } from 'console/features/device.map.track/ui/download';
import { CustomPath } from 'entities/device.map.track/ui/custom-path';
import { RenderWhenReady } from 'shared/components/render-when-ready';
import { TooltipForPath } from 'features/device.map.track/ui/path-tooltip';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { isAvailablePhotos, select_device_photo, } from 'entities/device.photos/model/get-photos-actions';
import { useAction, useAppSelector, usePartialAppSelector, } from 'shared/hooks/redux/hooks';
import { async_select_tracking_gps_and_icons, select_confirmed_viewport, select_hover_points_on_map, } from 'entities/device.map.track/lib/selectors';
import { useAPI } from 'console/shared/api/api-context';
import { selectDevice } from 'entities/device/lib';
import { select_current_account_id } from 'console/redux/selectors';
import { hoverPointsOnMap } from 'entities/device.map.track/model/actions';
import { useIsFullscreen } from 'shared/map/ui/components/control-buttons/components/fullscreen/lib';
import { ErrorSnack } from 'console/dialogs/error/snake-error/snake';
import { LegacyDevice } from 'console/device/device';
const nullable_viewport = [0, 0];
export const DeviceTrack = memo(function DeviceTrack(props) {
    const { show_track, show_download_track, device_id } = props;
    const api = useAPI();
    const [path_tooltip_visible, noPathTooltipVisible, setPathTooltipVisible] = useBooleanState(false);
    const [path_tooltip_left, setPathTooltipLeft] = useState(100);
    const [path_tooltip_top, setPathTooltipTop] = useState(100);
    const [path_tooltip_bottom, setPathTooltipBottom] = useState('');
    const path_tooltip = useRef(null);
    const timeoutIdRef = useRef();
    const account_id = useAppSelector(select_current_account_id);
    const photos_from_server = useAppSelector(select_device_photo);
    const device = usePartialAppSelector(selectDevice, device_id);
    const device_name = useMemo(() => (device != null ? LegacyDevice.display_name(device) : null), [device?.name, device?.individual_device_name]);
    const confirmed_viewport = usePartialAppSelector(select_confirmed_viewport, device_id) ??
        nullable_viewport;
    const hover_points_on_map = usePartialAppSelector(select_hover_points_on_map, device_id);
    const is_available_photos = useMemo(() => isAvailablePhotos(account_id, device), [
        account_id,
        device?.camera_available,
        device?.timezone,
        device?.access,
        device?.user_id,
    ]);
    const gps_and_events = usePartialAppSelector(async_select_tracking_gps_and_icons, device_id, api);
    const onHoverPointsOnMap = useAction(hoverPointsOnMap);
    //Ведём мышь по пути и нам всплывает посказка
    const onPathMouseMove = useCallback((points, pix) => {
        if (device_id != null) {
            if (points.length > 0) {
                onHoverPointsOnMap(device_id, points);
            }
        }
        if (path_tooltip.current != null) {
            const parent = path_tooltip.current.offsetParent;
            if (parent != null &&
                pix != null &&
                pix.y - 14 + path_tooltip.current.offsetHeight >
                    parent.offsetHeight - 10) {
                setPathTooltipTop('');
                setPathTooltipLeft(pix.x);
                setPathTooltipBottom(10);
            }
            else {
                if (pix != null) {
                    setPathTooltipTop(pix.y - 14);
                    setPathTooltipLeft(pix.x);
                    setPathTooltipBottom('');
                }
            }
        }
    }, [device_id]);
    //Пол-секунды подержим мышь над картой и покажем подсказку
    const onPathMouseOver = useCallback(() => (timeoutIdRef.current = setTimeout(setPathTooltipVisible, 300)), []);
    const onPathMouseOut = useCallback(() => {
        const timeoutId = timeoutIdRef.current;
        if (timeoutId) {
            timeoutIdRef.current = undefined;
            clearTimeout(timeoutId);
            noPathTooltipVisible();
        }
        if (device_id != null) {
            onHoverPointsOnMap(device_id, []);
        }
    }, [device_id]);
    const isFullScreen = useIsFullscreen();
    const showError = useCallback((error) => (_jsx(ErrorSnack, { error: error, open: true, sx: {
            zIndex: 401,
            position: 'absolute',
            left: 'calc(50% - 25px)',
            top: 'calc(50% - 25px)',
        } })), []);
    if (!show_track) {
        return null;
    }
    return (_jsx(RenderWhenReady, { promise: gps_and_events, loaderComponent: _jsx(CircularProgress, { size: 50, color: "primary", sx: {
                zIndex: 401,
                position: 'absolute',
                left: 'calc(50% - 25px)',
                top: 'calc(50% - 25px)',
            } }), errorComponent: showError, clear_on_promise_change: false, children: ([gps, events]) => (_jsxs(_Fragment, { children: [gps.length > 0 && (_jsxs(_Fragment, { children: [!isFullScreen && show_download_track && (_jsx(DownloadTrackButton, { device_id: device_id, device_name: device_name ?? 'безымянный', visible: gps.length > 0 && screen.height > 600, available_photos: is_available_photos })), _jsx(CustomPath, { points: gps, events: events, highlightTime: confirmed_viewport, onMouseMove: onPathMouseMove, onMouseOver: onPathMouseOver, onMouseOut: onPathMouseOut, device_id: device_id })] })), _jsx("div", { className: classnames('ui-track-tooltip', {
                        visible: path_tooltip_visible,
                    }), style: {
                        left: path_tooltip_left,
                        top: path_tooltip_top,
                        bottom: path_tooltip_bottom,
                    }, ref: path_tooltip, children: _jsx(TooltipForPath, { device_id: device_id, hover_points: hover_points_on_map, available_photos: is_available_photos, photos_from_server: photos_from_server }) })] })) }));
});
